/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { databaseService } from '../database/database.service.js';

class DagRepository {
  /**
   * Get all DAG checks for a goat (latest first)
   * @param {number} goatId
   * @param {number|null} limit
   * @returns {Promise<Array<import('../../models/dag.model.js').DagCheck>>}
   */
  async findByGoat(goatId, limit = null) {
    const base = 'SELECT * FROM dag WHERE goat_id = ? ORDER BY measured_at DESC';
    const sql = limit ? `${base} LIMIT ?` : base;
    const params = limit ? [goatId, limit] : [goatId];

    const res = await databaseService.query(sql, params);
    return (res.values || []).map(row => ({
      id: row.id,
      goatId: row.goat_id,
      score: row.score,
      measuredAt: row.measured_at,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    }));
  }

  /**
   * Get a single DAG check by ID
   */
  async findById(id) {
    const sql = 'SELECT * FROM dag WHERE id = ?';
    const res = await databaseService.query(sql, [id]);

    const row = (res.values || [])[0];
    if (!row) return null;

    return {
      id: row.id,
      goatId: row.goat_id,
      score: row.score,
      measuredAt: row.measured_at,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    };
  }

  /**
   * Get all DAG checks (latest first)
   */
  async findAll() {
    const sql = 'SELECT * FROM dag ORDER BY measured_at DESC';
    const res = await databaseService.query(sql);

    return (res.values || []).map(row => ({
      id: row.id,
      goatId: row.goat_id,
      score: row.score,
      measuredAt: row.measured_at,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    }));
  }

  /**
   * Create a new DAG check
   */
  async create({ goatId, score, measuredAt = null }) {
    const sql = measuredAt
      ? 'INSERT INTO dag (goat_id, score, measured_at, created_at, updated_at) VALUES (?, ?, ?, CURRENT_TIMESTAMP, CURRENT_TIMESTAMP)'
      : 'INSERT INTO dag (goat_id, score, created_at, updated_at) VALUES (?, ?, CURRENT_TIMESTAMP, CURRENT_TIMESTAMP)';
    const params = measuredAt ? [goatId, score, measuredAt] : [goatId, score];

    const res = await databaseService.run(sql, params);
    const id = res.changes?.lastId;

    return this.findById(id);
  }

  /**
   * Update an existing DAG check
   */
  async update(id, { score, measuredAt = null }) {
    const sql = measuredAt
      ? 'UPDATE dag SET score = ?, measured_at = ?, updated_at = CURRENT_TIMESTAMP WHERE id = ?'
      : 'UPDATE dag SET score = ?, updated_at = CURRENT_TIMESTAMP WHERE id = ?';
    const params = measuredAt ? [score, measuredAt, id] : [score, id];

    await databaseService.run(sql, params);
    return this.findById(id);
  }

  /**
   * Delete a DAG check
   */
  async delete(id) {
    await databaseService.run('DELETE FROM dag WHERE id = ?', [id]);
  }

  /**
   * Delete all DAG checks for a specific goat
   * @param {number} goatId
   */
  async deleteByGoat(goatId) {
    await databaseService.run('DELETE FROM dag WHERE goat_id = ?', [goatId]);
  }

  /**
   * Get the latest DAG check for a goat
   */
  async latest(goatId) {
    const rows = await this.findByGoat(goatId, 1);
    return rows[0] ?? null;
  }
}

export const dagRepository = new DagRepository();
